import { create } from 'zustand';
import axios from 'axios';
import { getBackendBaseUrl } from '../lib/backendUrl';

const API = `${getBackendBaseUrl()}/api/team-chat`;

export const useTeamChatStore = create((set, get) => ({
  messages: [],
  loading: false,
  sending: false,
  hasMore: false,
  error: null,
  pollingInterval: null,

  fetchMessages: async (orgId, { append = false } = {}) => {
    if (!orgId) return;
    set({ loading: true, error: null });
    try {
      const { data } = await axios.get(`${API}/${orgId}/messages`, { params: { limit: 100 } });
      set({ messages: data.messages || [], hasMore: data.has_more, loading: false });
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message, loading: false });
    }
  },

  loadMore: async (orgId) => {
    const msgs = get().messages;
    if (!msgs.length || !orgId) return;
    const oldest = msgs[0];
    try {
      const { data } = await axios.get(`${API}/${orgId}/messages`, {
        params: { before: oldest.id, limit: 50 },
      });
      const older = data.messages || [];
      set({ messages: [...older, ...msgs], hasMore: data.has_more });
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message });
    }
  },

  sendMessage: async (orgId, content, messageType = 'text', sharedPayload = null) => {
    if (!orgId || !content.trim()) return null;
    set({ sending: true });
    try {
      const { data } = await axios.post(`${API}/${orgId}/messages`, {
        content: content.trim(),
        message_type: messageType,
        shared_payload: sharedPayload,
      });
      set(s => ({ messages: [...s.messages, data], sending: false }));
      return data;
    } catch (e) {
      set({ sending: false, error: e.response?.data?.detail || e.message });
      return null;
    }
  },

  deleteMessage: async (orgId, messageId) => {
    try {
      await axios.delete(`${API}/${orgId}/messages/${messageId}`);
      set(s => ({ messages: s.messages.filter(m => m.id !== messageId) }));
    } catch (e) {
      set({ error: e.response?.data?.detail || e.message });
    }
  },

  startPolling: (orgId, intervalMs = 5000) => {
    const existing = get().pollingInterval;
    if (existing) clearInterval(existing);
    const id = setInterval(async () => {
      if (!orgId) return;
      try {
        const { data } = await axios.get(`${API}/${orgId}/messages`, { params: { limit: 100 } });
        const newMsgs = data.messages || [];
        const current = get().messages;
        if (newMsgs.length !== current.length || (newMsgs.length && current.length && newMsgs[newMsgs.length - 1]?.id !== current[current.length - 1]?.id)) {
          set({ messages: newMsgs, hasMore: data.has_more });
        }
      } catch { /* silent */ }
    }, intervalMs);
    set({ pollingInterval: id });
  },

  stopPolling: () => {
    const id = get().pollingInterval;
    if (id) clearInterval(id);
    set({ pollingInterval: null });
  },

  clear: () => {
    const id = get().pollingInterval;
    if (id) clearInterval(id);
    set({ messages: [], loading: false, sending: false, hasMore: false, error: null, pollingInterval: null });
  },
}));
